import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";

import { submitSupportMessage } from "@/lib/support.functions";
import { useOptionalAuth } from "@/lib/auth-context";

import { PageHero } from "@/components/page-hero";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact Quibblies" },
      {
        name: "description",
        content:
          "Contact the Quibblies team about your account, a moderation decision, a bug, press or a privacy request.",
      },
      { property: "og:title", content: "Contact Quibblies" },
      {
        property: "og:description",
        content: "Get in touch with the Quibblies team about accounts, moderation or privacy.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [topic, setTopic] = React.useState("account");
  const [sending, setSending] = React.useState(false);
  const auth = useOptionalAuth();
  const send = useServerFn(submitSupportMessage);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    setSending(true);
    try {
      await send({
        data: {
          name: String(values.get("name") ?? ""),
          email: String(values.get("email") ?? ""),
          topic,
          message: String(values.get("message") ?? ""),
          userId: auth?.user?.id ?? null,
        },
      });
      form.reset();
      toast.success("Message received", {
        description:
          "We have logged your message and sent a confirmation. The team usually replies within 2 working days.",
      });
    } catch (error) {
      toast.error("Could not send your message", {
        description: error instanceof Error ? error.message : "Please try again in a moment.",
      });
    } finally {
      setSending(false);
    }
  }

  return (
    <div>
      <PageHero
        eyebrow="Contact"
        title="Talk to the Quibblies team"
        description="Accounts, moderation decisions, bugs, privacy requests and press. For copyright claims please use the dedicated removal process."
      />

      <div className="mx-auto max-w-3xl px-4 py-14 sm:px-6">
        <form
          onSubmit={handleSubmit}
          className="space-y-5 rounded-[1.75rem] border border-border/70 bg-gradient-card p-6 sm:p-8"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="contact-name">Your name</Label>
              <Input id="contact-name" name="name" required autoComplete="name" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                name="email"
                type="email"
                required
                autoComplete="email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-topic">Topic</Label>
            <Select value={topic} onValueChange={setTopic}>
              <SelectTrigger id="contact-topic">
                <SelectValue placeholder="Choose a topic" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="account">Account and approval</SelectItem>
                <SelectItem value="moderation">A moderation decision</SelectItem>
                <SelectItem value="appeal">Appealing a decision</SelectItem>
                <SelectItem value="safety">Safety or abuse report</SelectItem>
                <SelectItem value="privacy">Privacy or data request</SelectItem>
                <SelectItem value="bug">Something is broken</SelectItem>
                <SelectItem value="press">Press and partnerships</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contact-message">Message</Label>
            <Textarea id="contact-message" name="message" rows={7} required />
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" variant="hero" size="pill" disabled={sending}>
              {sending ? "Sending…" : "Send message"}
            </Button>
            <p className="text-xs text-muted-foreground">
              Check the{" "}
              <Link to="/support" className="text-primary-glow underline-offset-4 hover:underline">
                Support &amp; FAQ
              </Link>{" "}
              first. Copyright claim?{" "}
              <Link
                to="/copyright"
                className="text-primary-glow underline-offset-4 hover:underline"
              >
                Use the removal process
              </Link>
              .
            </p>
          </div>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Prefer email? Write to{" "}
          <a
            href="mailto:quibbliessupport@gmail.com"
            className="text-primary-glow underline-offset-4 hover:underline"
          >
            quibbliessupport@gmail.com
          </a>
          .
        </p>
      </div>
    </div>
  );
}
